import type { MemberStatement, MemberStatementGroup } from "@/services/types";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatINR } from "@/utils/format";
import { payoutStatusShort } from "@/pages/payout/components/labels";
import { Badge } from "@/components/base/Badge";

function Money({ value }: { value: number }) {
  return <CurrencyDisplay amount={value} hideSymbol className="text-sm font-medium" />;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="mb-2.5 border-b border-background-200 pb-1.5 text-sm font-bold uppercase tracking-wide text-foreground-700">{children}</h3>;
}

export function GroupStatementPrint({ statement }: { statement: MemberStatement }) {
  const g = statement.group;
  return (
    <div>
      {/* Member + group header */}
      <div className="mb-5 grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm text-foreground-800">
        <p><span className="text-foreground-500">Member:</span> <span className="font-semibold text-foreground-950">{statement.memberName}</span></p>
        <p><span className="text-foreground-500">Code:</span> <span className="tnum font-medium">{statement.memberCode}</span></p>
        <p><span className="text-foreground-500">Phone:</span> <span className="tnum font-medium">{statement.memberPhone}</span></p>
        <p><span className="text-foreground-500">City:</span> {statement.city}</p>
        <p><span className="text-foreground-500">Group:</span> <span className="font-semibold text-foreground-950">{g.groupName} ({g.groupCode})</span></p>
        <p><span className="text-foreground-500">Ticket:</span> <span className="tnum font-medium">{g.ticketNumber}</span></p>
        <p><span className="text-foreground-500">Monthly installment:</span> <span className="tnum font-semibold">{formatINR(g.monthlyInstallment)}</span></p>
        <p><span className="text-foreground-500">Statement generated:</span> <span className="tnum font-medium">{formatDate(statement.generatedAt)}</span></p>
      </div>

      {/* Cycle-wise table */}
      <SectionTitle>Cycle-wise statement</SectionTitle>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-background-100 text-foreground-600">
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Cycle</th>
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Period</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Installment</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Dividend</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Commission</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Net payable</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Paid</th>
            <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Date / Receipt</th>
            <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Balance</th>
          </tr>
        </thead>
        <tbody>
          {g.cycles.map((c) => (
            <tr key={c.cycle} className="border-t border-background-100">
              <td className="px-3 py-2 tnum font-medium">
                {c.cycle}
                {c.won ? <span className="ml-1 text-success-600 font-semibold">(won)</span> : null}
                {c.isReserved ? <span className="ml-1 text-foreground-400">(reserved)</span> : null}
              </td>
              <td className="whitespace-nowrap px-3 py-2">{c.periodLabel}</td>
              <td className="px-3 py-2 text-right"><Money value={c.baseInstallment} /></td>
              <td className="px-3 py-2 text-right text-success-600 font-medium"><Money value={c.dividendCredit} /></td>
              <td className="px-3 py-2 text-right font-medium text-foreground-700"><Money value={c.commissionPerMember ?? 0} /></td>
              <td className="px-3 py-2 text-right font-semibold"><Money value={c.netPayable} /></td>
              <td className="px-3 py-2 text-right"><Money value={c.paid} /></td>
              <td className="whitespace-nowrap px-3 py-2 text-foreground-600">
                {c.paymentDate ? <span className="tnum">{formatDate(c.paymentDate)}</span> : <span className="text-foreground-400">—</span>}
                {c.receiptNumber ? <span className="tnum ml-1 text-foreground-500 font-medium">({c.receiptNumber})</span> : null}
              </td>
              <td className="px-3 py-2 text-right font-medium"><Money value={c.balance} /></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-background-200 bg-background-100/60 font-semibold">
            <td className="px-3 py-2.5 text-foreground-600 uppercase text-xs tracking-wider" colSpan={2}>Total</td>
            <td className="px-3 py-2.5 text-right"><Money value={g.totalCharged} /></td>
            <td className="px-3 py-2.5 text-right text-success-600"><Money value={g.totalDividend} /></td>
            <td className="px-3 py-2.5 text-right font-medium"><Money value={g.totalCommission ?? g.cycles.reduce((s, c) => s + (c.commissionPerMember ?? 0), 0)} /></td>
            <td className="px-3 py-2.5 text-right font-bold"><Money value={g.totalNetPayable ?? g.cycles.reduce((s, c) => s + c.netPayable, 0)} /></td>
            <td className="px-3 py-2.5 text-right font-bold"><Money value={g.totalPaid} /></td>
            <td className="px-3 py-2.5" />
            <td className="px-3 py-2.5 text-right font-bold"><Money value={g.totalOutstanding} /></td>
          </tr>
        </tfoot>
      </table>

      {/* Payout section */}
      {g.payouts.length > 0 ? (
        <div className="mt-6">
          <SectionTitle>Payouts received</SectionTitle>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-background-100 text-foreground-600">
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Cycle</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Period</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Voucher</th>
                <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Prize</th>
                <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Dues adjusted</th>
                <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Net received</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {g.payouts.map((p) => (
                <tr key={p.voucherNumber} className="border-t border-background-100">
                  <td className="tnum px-3 py-2 font-medium">{p.cycle}</td>
                  <td className="whitespace-nowrap px-3 py-2">{p.periodLabel}</td>
                  <td className="tnum px-3 py-2 font-mono font-semibold text-primary-700">{p.voucherNumber}</td>
                  <td className="px-3 py-2 text-right"><Money value={p.prize} /></td>
                  <td className="px-3 py-2 text-right text-danger-600"><Money value={p.duesAdjusted} /></td>
                  <td className="px-3 py-2 text-right font-semibold"><Money value={p.netReceived} /></td>
                  <td className="tnum px-3 py-2">{p.paidOn ? formatDate(p.paidOn) : "—"}</td>
                  <td className="px-3 py-2"><Badge color={p.status === "reversed" ? "danger" : "success"}>{payoutStatusShort(p.status)}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Summary */}
      <div className="mt-6">
        <SectionTitle>Summary</SectionTitle>
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-background-200 bg-background-200 sm:grid-cols-3">
          <SummaryCell label="Total charged" value={formatINR(g.totalCharged)} />
          <SummaryCell label="Total dividend received" value={formatINR(g.totalDividend)} />
          <SummaryCell label="Total commission" value={formatINR(g.totalCommission ?? g.cycles.reduce((s, c) => s + (c.commissionPerMember ?? 0), 0))} />
          <SummaryCell label="Total net payable" value={formatINR(g.totalNetPayable ?? g.cycles.reduce((s, c) => s + c.netPayable, 0))} />
          <SummaryCell label="Total paid" value={formatINR(g.totalPaid)} />
          <SummaryCell label="Total outstanding" value={formatINR(g.totalOutstanding)} danger={g.totalOutstanding > 0} />
          <SummaryCell label="Won the pool" value={g.wonCycle != null ? `Cycle ${g.wonCycle}` : "Not yet"} />
          <SummaryCell label="Cycles remaining" value={`${g.cyclesRemaining}`} />
        </div>
      </div>
    </div>
  );
}

function SummaryCell({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="bg-background-50 p-3.5">
      <p className="text-xs uppercase tracking-wider text-foreground-500 font-medium">{label}</p>
      <p className={`tnum mt-1 text-base font-bold ${danger ? "text-danger-600" : "text-foreground-950"}`}>{value}</p>
    </div>
  );
}